/** Overview: headline stat cards + the most recently joined players. */
import { el, clear } from '../../dom';
import { adminApi, type AdminStats, type AdminTrends } from '../../game-api';
import { fmtNum, fmtDateTime } from './format';
import { grid, loadInto, panel, pill, statCard, table, emptyBox } from './ui';
import { barChart, sum } from './charts';

export function renderOverview(host: HTMLElement): () => void {
  return loadInto(host, () => adminApi.stats(), (s) => view(s), "Couldn't load the dashboard stats.");
}

/**
 * The four activity graphs: signups, daily actives, battles and revenue.
 *
 * One card each rather than a combined chart — they differ by orders of magnitude
 * (a handful of signups against thousands of battles), so a shared axis would
 * flatten the smaller series onto the baseline and hide exactly the movement an
 * operator is looking for.
 */
function trendPanels(t: AdminTrends): HTMLElement {
  const days = t.points.map((p) => p.day);
  const card = (title: string, values: number[], color: string, total: string) =>
    panel(title,
      el('div', 'flex items-baseline gap-2 mb-2', {},
        el('span', 'font-heading text-gold text-lg tabular-nums', {}, total),
        el('span', 'text-[11px] text-text-dim font-mono', {}, `last ${t.days} days`)),
      barChart(days, values, { color }));

  const eur = (cents: number) => `€${(cents / 100).toFixed(2)}`;
  const g = grid(280);
  g.append(
    card('New players', t.points.map((p) => p.signups), 'var(--color-gold, #ffd97a)',
      fmtNum(sum(t.points.map((p) => p.signups)))),
    card('Daily active players', t.points.map((p) => p.active), '#7fd1a6',
      fmtNum(Math.max(0, ...t.points.map((p) => p.active)))),
    card('Battles', t.points.map((p) => p.battles), '#e88b6a',
      fmtNum(sum(t.points.map((p) => p.battles)))),
    card('Revenue', t.points.map((p) => p.revenueCents), '#9db8ff',
      eur(sum(t.points.map((p) => p.revenueCents)))),
  );
  return g;
}

function view(s: AdminStats): HTMLElement {
  const cards = grid(190);
  cards.append(
    statCard('Total players', fmtNum(s.totalPlayers)),
    statCard('New today', fmtNum(s.newPlayers?.today), `${fmtNum(s.newPlayers?.week)} this week`),
    statCard('New this month', fmtNum(s.newPlayers?.month)),
    statCard('Online now', fmtNum(s.onlineNow)),
    statCard('Battles today', fmtNum(s.battlesToday)),
    statCard('Circulating gold', fmtNum(s.circulatingGold), `${fmtNum(s.circulatingShrooms)} shrooms`),
  );

  const recent = s.recentPlayers ?? [];
  const recentPanel = panel('Recent players', recent.length
    ? table(['Nickname', 'Email', 'Level', 'Joined', 'Status'], recent.map((p) => [
      p.nickname ?? '—',
      el('span', 'text-text-dim', {}, p.email),
      String(p.level ?? 0),
      fmtDateTime(p.joinedAt),
      p.banned ? pill('banned', 'bad') : pill('active', 'ok'),
    ]))
    : emptyBox('No players yet.'));

  // The graphs load separately from the stat cards: they are a second query, and a
  // slow trends read must not hold up the headline numbers an operator opens the
  // page for.
  const graphs = el('div', 'flex flex-col gap-4', {}, el('div', 'text-text-dim text-sm', {}, 'Loading activity…'));
  void adminApi.trends(30)
    .then((t) => { clear(graphs); graphs.append(trendPanels(t)); })
    .catch(() => { clear(graphs); graphs.append(emptyBox("Couldn't load the activity graphs.")); });

  return el('div', 'flex flex-col gap-4', {}, cards, graphs, recentPanel);
}
